import cx from 'classnames'
import {ReactComponent as CalendarIcon} from 'icons/calendar.svg'
import {ReactComponent as ClockIcon} from 'icons/clock.svg'
import {ReactComponent as InfoIcon} from 'icons/info-circle.svg'
import {ReactComponent as UsersIcon} from 'icons/users.svg'
import {useShowSocialProofBadges} from 'providers/EnvironmentProvider'
import {ReactNode, useMemo} from 'react'
import {Exp1, Ventrata} from 'types'
import {formatAvailabilityId} from 'utils/formatAvailabilityId'
import {formatMoney} from 'utils/formatMoney'
import {formatUnitItems} from 'utils/formatUnitItems'
import {Card} from './Card'
import SocialProofBadge from './CartItem/SocialProofBadge'
import {Image} from './Image'
import LocalisedDate from './LocalisedDate'
import LocalisedTime from './LocalisedTime'

type Props = {
  booking: Ventrata.Booking
  exp1Product: Exp1.Product | null
  children?: ReactNode
  className?: string
}

export const CartItemData = ({booking, exp1Product, children, className}: Props) => {
  const showSocialProofBadges = useShowSocialProofBadges()

  const optionTitle = useMemo(() => {
    if (exp1Product) {
      return (
        <a className="font-bold text-lg" href={exp1Product.permalink} target="_blank" rel="noopener noreferrer">
          {exp1Product.name}
        </a>
      )
    }

    return <div className="font-bold text-lg">{booking.product.title}</div>
  }, [exp1Product, booking])

  const optionImage = useMemo(() => {
    if (exp1Product) {
      return (
        <a className="font-bold text-lg" href={exp1Product.permalink} target="_blank" rel="noopener noreferrer">
          <Image src={booking.option.coverImageUrl} title={booking.option.title} />
        </a>
      )
    }

    return <Image src={booking.option.coverImageUrl} title={booking.option.title} />
  }, [exp1Product, booking])

  return (
    <Card
      className={cx(className, 'cart-item-container')}
      image={optionImage}
      header={
        <div className="flex flex-col gap-2">
          <div className="option-title-group">
            <SocialProofBadge className={cx({hidden: !showSocialProofBadges})} type="urgent" />
            {optionTitle}
          </div>

          {booking.option.duration && (
            <div className="hidden @[407px]:flex items-center gap-2 text-gray-400">
              <ClockIcon />
              <span>{booking.option.duration}</span>
            </div>
          )}
        </div>
      }
      main={
        <div className="flex flex-col gap-4 text-xs">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <UsersIcon />
              <div>{formatUnitItems(booking.unitItems)}</div>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon />
              <div>
                <LocalisedDate date={formatAvailabilityId(booking.availabilityId)} />
                {' • '}
                <LocalisedTime date={formatAvailabilityId(booking.availabilityId)} />
              </div>
            </div>
            {booking.option && booking.option.title && (
              <div className="flex items-center gap-2">
                <InfoIcon />
                <div>{booking.option.title}</div>
              </div>
            )}
          </div>
          <div className="flex justify-between items-center">
            {children || <div />}
            <div className="flex flex-col items-end">
              <div className="font-bold">{formatMoney(booking.pricing?.original, booking.pricing?.currency)}</div>
            </div>
          </div>
        </div>
      }
    />
  )
}
